<script lang="ts">
export const inputCls = cva("input aria-invalid:input-error", {
	variants: {
		color: {
			default: null,
			neutral: "input-neutral",
			primary: "focus:input-primary",
			secondary: "focus:input-secondary",
			accent: "focus:input-accent",
			info: "focus:input-info",
			success: "focus:input-success",
			warning: "focus:input-warning",
			error: "focus:input-error",
		},
		size: {
			xs: "input-xs [&_svg]:icon-xs [&_kbd]:kbd-xs",
			sm: "input-sm [&_svg]:icon-sm [&_kbd]:kbd-xs",
			md: "input-md [&_svg]:icon-md [&_kbd]:kbd-sm",
			lg: "input-lg [&_svg]:icon-lg [&_kbd]:kbd-md",
		},
		variant: {
			default: null,
			ghost: "input-ghost",
		},
	},
	defaultVariants: {
		size: "md",
		color: "default",
	},
});

type variant = VariantProps<typeof inputCls>;

export interface InputVariantProps {
	color?: variant["color"];
	variant?: variant["variant"];
	size?: variant["size"];
}

export interface InputProps extends ClassProps, InputVariantProps, DeprecatedSizeProps {}

interface Props extends InputProps, /* @vue-ignore */ Omit<InputHTMLAttributes, keyof InputProps> {}
</script>

<script setup lang="ts">
import { cva, type VariantProps } from "class-variance-authority";
import { useForwardExpose } from "reka-ui";
import { computed, inject, useAttrs, type InputHTMLAttributes } from "vue";
import { InputGroupSymbol } from "../../injectionSymbols";
import type { ClassProps } from "../../lib/base-props";
import { convertDeprecatedSize, type DeprecatedSizeProps } from "../../lib/size";
import { cn, htmlAttrs } from "../../lib/utils";

const inGroup = inject(InputGroupSymbol, false);

const props = withDefaults(defineProps<Props>(), {
	color: "default",
	size: "md",
}) as InputProps;
const attrs = useAttrs() as InputHTMLAttributes;

defineOptions({ inheritAttrs: false });

const model = defineModel<any>();

const size = computed(() => {
	if (props.size != null) {
		return props.size;
	}

	return convertDeprecatedSize(props);
});

const { forwardRef } = useForwardExpose();
</script>

<template>
	<input
		:ref="forwardRef"
		v-model="model"
		:class="inGroup ? props.class : cn(inputCls({ ...props, size }))"
		placeholder=" "
		:data-size="size"
		:data-color="props.color"
		:data-variant="props.variant"
		v-bind="htmlAttrs(attrs)"
	/>
</template>
